import { AnyIfEmpty, useDispatch, useSelector } from 'react-redux';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import rrulePlugin from '@fullcalendar/rrule';
import timeGridPlugin from '@fullcalendar/timegrid';
import resourcePlugin from '@fullcalendar/resource';
import moment from 'moment';
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { eventColors, resources10Lockers, resources12Lockers } from '../../../constants/constants';
import { getAvailabilities } from '../../../redux/availability/actions/availability.action';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import CustModal from '../../common/custmodal/CustModal';
import styles from './AvailabilityFullCalendar.module.css';
import AvailabilityPopup from './AvailabilityPopup';
import DeleteAvailabilityModal from './DeleteAvailability/DeleteAvailabilityModal';
import Loader from '../../common/loader/Loader';

import { DatesSetArg, EventApi, MoreLinkContentArg } from '@fullcalendar/core';
import { eventData } from './types';
import AvailabilityTable from './AvailabilityTable/AvailabilityTable';

interface props {
    locationId: string;
    lockersCount: number;
    handleDatesSet?: (args: DatesSetArg) => void;
    handleDateRangeChange?: (startDate: string, endDate: string) => void;
    viewMode?: 'calendar' | 'list';
}

const AvailabilityFullCalendar = ({ locationId, lockersCount, handleDatesSet, handleDateRangeChange, viewMode = 'list' }: props) => {
    const dispatch: AppDispatch = useDispatch();
    const availabilityListLoading = useSelector((state: IRootState) => state.availability.availabilies.loading);
    const availabilityRootLoading = useSelector((state: IRootState) => state.availability.loading);
    const [calendarInfo, setCalendarInfo] = useState<EventApi | null>(null);
    const availabilities = useSelector((state: IRootState) => state.availability.availabilies.data);
    const [open, setOpen] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const calendarRef = useRef<FullCalendar>(null);
    const fetchedLocationIdRef = useRef<string | null>(null);
    const prevLocationIdRef = useRef<string | null>(null);

    // Memoize resources to prevent recreation on every render
    const resources = useMemo(() => {
        return lockersCount === 10 ? resources10Lockers : resources12Lockers;
    }, [lockersCount]);

    // Prevent duplicate API calls
    useEffect(() => {
        // Only fetch if locationId exists, is different from previous, and is not empty
        if (locationId && locationId.trim() !== '' && fetchedLocationIdRef.current !== locationId) {
            fetchedLocationIdRef.current = locationId;
            dispatch(getAvailabilities(locationId));
        }
    }, [locationId, dispatch]);

    // After property change: reset calendar view to current month (list filters reset via table key)
    useEffect(() => {
        if (!locationId) return;
        if (prevLocationIdRef.current === null) {
            prevLocationIdRef.current = locationId;
            return;
        }
        if (prevLocationIdRef.current === locationId) return;
        prevLocationIdRef.current = locationId;

        const api = calendarRef.current?.getApi();
        if (api) {
            api.gotoDate(moment().startOf('month').toDate());
        }
    }, [locationId]);

    // Memoize sorted lockers - only recompute when availabilities actually change
    const sortedLockers = useMemo(() => {
        if (!availabilities || !Array.isArray(availabilities) || availabilities.length === 0) {
            return [];
        }
        // Create a stable sorted array
        return [...availabilities].sort((a: eventData, b: eventData) => a.locker_number - b.locker_number);
    }, [availabilities]);

    const handleEventClick = useCallback((e: AnyIfEmpty<object>) => {
        setOpen(true);
        setCalendarInfo(e.event);
    }, []);

    const handleDeleteClick = useCallback((item: eventData) => {
        const mockEvent = {
            extendedProps: {
                ...item,
                availabilityId: (item as eventData).availabilityId,
            },
        } as unknown as EventApi;

        setCalendarInfo(mockEvent);
        setShowDeletePopup(true);
    }, []);

    // Handle calendar resize when sidebar expands/collapses
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
                // Small delay to ensure DOM has updated
                setTimeout(() => {
                    calendarApi.updateSize();
                }, 100);
            }
        });

        const calendarContainer = document.querySelector('.outletConainer');
        if (calendarContainer) {
            resizeObserver.observe(calendarContainer);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // Memoize datesSet handler
    const handleDatesSetCallback = useCallback((args: DatesSetArg) => {
        handleDatesSet?.(args);
    }, [handleDatesSet]);

    // Memoize moreLinkContent handler
    const moreLinkContentCallback = useCallback((args: MoreLinkContentArg) => {
        return {
            html: `<div class='custom-more-visits'>${args?.shortText} <span>more</span></div>`,
        };
    }, []);

    // Memoize events array to prevent unnecessary re-renders
    const calendarEvents = useMemo(() => {
        return sortedLockers.map((eventData: eventData) => {
            const normalizedClosingTime = eventData.closing_time === '00:00' ? '23:59' : eventData.closing_time;

            return {
                ...eventData,
                resourceId: resources.find(
                    (resource) => resource.locker_number === eventData.locker_number,
                )?.id,
                title: `${eventData.unitYardiId ? eventData.unitYardiId : eventData.unit_number}-${eventData.locker_number}`,
                // title: `${eventData.unit_number} - `,
                startTime: eventData.opening_time,
                endTime: normalizedClosingTime,
                startRecur: eventData.start_date,
                endRecur: eventData.end_date,
                rrule: {
                    freq: 'daily',
                    dtstart: moment(`${eventData.start_date} ${eventData.opening_time}`, 'YYYY-MM-DD HH:mm a').toISOString(),
                    until: moment(`${eventData.end_date} ${normalizedClosingTime}`, 'YYYY-MM-DD HH:mm a').toISOString(),
                },
                duration: moment
                    .utc(moment(eventData.closing_time, 'HH:mm A').diff(moment(eventData.opening_time, 'HH:mm A')))
                    .format('HH:mm A'),
                backgroundColor: eventColors.find((event) => event.locker_number === eventData.locker_number)?.backgroundColor,
                textColor: '#000000',
                classNames: [
                    'calendarTitle',
                    eventColors.find((event) => event.locker_number === eventData.locker_number)?.backgroundCss || '',
                ],
                borderColor: 'transparent',
            };
        });
    }, [sortedLockers, resources]);

    const showAvailabilityLoader = availabilityListLoading || availabilityRootLoading || isLoading;

    return (
        <div className={styles.rootWrap}>
            {showAvailabilityLoader && <Loader />}

            <CustModal
                open={open}
                setOpen={setOpen}
                bodyData={
                    <AvailabilityPopup
                        locationId={locationId}
                        setOpen={setOpen}
                        calendarInfo={calendarInfo}
                        setShowDeletePopup={setShowDeletePopup}
                    />
                }
            />

            <CustModal
                open={showDeletePopup}
                setOpen={setShowDeletePopup}
                bodyData={
                    <DeleteAvailabilityModal
                        setIsLoading={setIsLoading}
                        setOpen={setShowDeletePopup}
                        availabilityId={calendarInfo?.extendedProps?.availabilityId as string}
                        locationId={locationId}
                    />
                }
            />
            {viewMode === 'calendar' ? (
                <div className={styles.container}>
                    <FullCalendar
                        ref={calendarRef}
                        datesSet={handleDatesSetCallback}
                        initialView="dayGridMonth"
                        dayMaxEvents={3}
                        eventClick={handleEventClick}
                        selectable={true}
                        plugins={[rrulePlugin, dayGridPlugin, timeGridPlugin, resourceTimeGridPlugin, interactionPlugin, resourcePlugin]}
                        allDaySlot={false}
                        navLinks={true}
                        // monthMode={false}
                        eventOrder={'eventProperty'}
                        moreLinkClick={() => 'resourceTimeGridDay'}
                        moreLinkContent={moreLinkContentCallback}
                        displayEventTime={false}
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: 'dayGridMonth,timeGridWeek,resourceTimeGridDay',
                        }}
                        height="100% !important"
                        resources={resources}
                        slotEventOverlap={true}
                        events={calendarEvents}
                    />
                </div>
            ) : (
                <AvailabilityTable
                    key={locationId}
                    sortedLockers={sortedLockers}
                    handleDeleteClick={handleDeleteClick}
                    onDateRangeChange={handleDateRangeChange}
                />
            )}
        </div>
    );
};

export default AvailabilityFullCalendar;
